/* 
██╗██╗░░░██╗  ██████╗░██╗░██████╗░██╗████████╗░█████╗░██╗░░░░░
██║██║░░░██║  ██╔══██╗██║██╔════╝░██║╚══██╔══╝██╔══██╗██║░░░░░
██║██║░░░██║  ██║░░██║██║██║░░██╗░██║░░░██║░░░███████║██║░░░░░
██║██║░░░██║  ██║░░██║██║██║░░╚██╗██║░░░██║░░░██╔══██║██║░░░░░
██║╚██████╔╝  ██████╔╝██║╚██████╔╝██║░░░██║░░░██║░░██║███████╗
╚═╝░╚═════╝░  ╚═════╝░╚═╝░╚═════╝░╚═╝░░░╚═╝░░░╚═╝░░╚═╝╚══════╝
╔════════════════════════════════════════════════════════════╗
║              EQUIPO DE DESARROLLO DE SOFTWARE              ║
║                   MODULO DESARROLLADO POR                  ║
╟┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄╢
║ Victor Manuel Calle Londoño                                ║
║ victor.calle@est.iudigital.edu.co                          ║
╚════════════════════════════════════════════════════════════╝
*/

/**
 * Componente modal para editar una fuente de datos existente.
 *
 * Este componente permite actualizar la información de una fuente de datos,
 * incluyendo nombre, descripción, ubicación, dirección, horarios de check-in/out
 * y la imagen strip del PKPass. Los campos de ubicación y dirección son obligatorios
 * solo para carnets tipo Invitado o Visitante.
 *
 * Campos editables:
 * - source_name: Nombre de la fuente (máx. 255 caracteres)
 * - source_details: Descripción (máx. 500 caracteres)
 * - location: Ubicación (máx. 25 caracteres, obligatorio para Invitado/Visitante)
 * - address: Dirección (máx. 100 caracteres, obligatorio para Invitado/Visitante)
 * - check_in_time/check_out_time: Horarios de entrada y salida
 * - strip_image: Imagen PNG para PKPass (máx. 2MB)
 *
 * @component
 * @author Victor Manuel Calle Londoño <victor.calle@est.iudigital.edu.co>
 * @date [Fecha de creación: 2023-10-18]
 * @updated [Fecha de última modificación: 2025-10-25]
 * @updatedBy Eulicer Zapata <eulicer.zapata@iudigital.edu.co>
 */

import {
  Box,
  Card,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  Icon,
  MenuItem,
  Modal,
  Radio,
  RadioGroup,
  Select,
  Typography,
  styled,
} from "@mui/material";
import colors from "assets/theme/base/colors";
import closeImg from "assets/images/close_X.svg";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import React, { useState } from "react";
import PropTypes from "prop-types";
import TimeSelectComponent from "components/TimeSelect/TimeSelectComponent";
import MDBox from "components/MDBox";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import ToolTip from "components/ToolTip";

export function SourceEdit({
  open,
  handleClose,
  datosRecuperados,
  datosEditados,
  isFormModified,
  isDisabled,
  getFieldValue,
  handleFieldChange,
  handleSubmit,
  stripImageUrl,
  stripImageFile,
  handleStripImageChange,
  isFieldValid,
  fieldErrors,
  recoveredData,
}) {
  // Determinar si es tipo Invitado o Visitante (DEBE ir primero)
  const requiresGuestFields =
    getFieldValue("perfil") === "Invitado" || getFieldValue("perfil") === "Visitante";

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    maxWidth: requiresGuestFields ? "700px" : "700px",
    borderRadius: "10px",
    boxShadow: 24,
    p: 4,
    maxHeight: "90vh",
  };

  const content = {
    margin: "0",
    padding: "0",
    display: "grid",
    align: "center",
    width: "100%",
    gap: "15px",
  };

  const boxCommonStyles = {
    width: "100%",
    padding: requiresGuestFields ? "1px" : "16px 32px",
    borderRadius: "8px",
    transition: "all 0.2s ease-in-out",
  };

  const inputStyles = {
    width: "100%",
    "& .MuiInputBase-root": {
      minHeight: "45px",
    },
    "& .MuiInputBase-input": {
      padding: "12px 14px",
    },
  };

  const labelStyles = {
    color: "black",
    fontWeight: 500,
    marginBottom: "4px",
    fontSize: "0.95rem",
    display: "block",
  };

  const HtmlTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: "#f5f5f9",
      color: "rgba(0, 0, 0, 0.87)",
      maxWidth: 220,
      fontSize: theme.typography.pxToRem(12),
      border: "1px solid #dadde9",
    },
  }));

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Card sx={style}>
        <MDBox
          mt={-7}
          color="white"
          bgColor="info"
          variant="gradient"
          borderRadius="lg"
          p={2}
          sx={{ display: "flex", justifyContent: "flex-end" }}
        >
          <Typography id="modal-modal-title" component="h2">
            Actualizar fuente de datos: {recoveredData?.source_name}
          </Typography>
          <Icon
            sx={{
              fontSize: "2em",
              stroke: "currentColor",
              strokeWidth: "2px",
              cursor: "pointer",
              marginLeft: "auto",
            }}
            onClick={handleClose}
          >
            close
          </Icon>
        </MDBox>
        <Divider variant="middle" />
        <div style={{ maxHeight: "80%", overflowY: "auto", justifyContent: "center" }}>
          <form onSubmit={handleSubmit}>
            <MDBox
              sx={content}
              style={{
                justifyContent: "space-around",
              }}
            >
              {/* Sección: Información General */}
              <Box sx={boxCommonStyles}>
                {/* Primera fila: Nombre y Descripción */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "1fr 1fr",
                    },
                    gap: 2,
                    mb: requiresGuestFields ? 2 : 0,
                  }}
                >
                  {/* Nombre de la fuente */}
                  <Box>
                    <Typography sx={labelStyles}>Nombre de la fuente *</Typography>
                    <MDInput
                      type="text"
                      value={getFieldValue("source_name") || ""}
                      onChange={(e) => handleFieldChange("source_name", e.target.value)}
                      disabled={isDisabled}
                      fullWidth
                      inputProps={{ maxLength: 255 }}
                      error={
                        (isFieldValid && !isFieldValid.source_name) || !!fieldErrors?.source_name
                      }
                      sx={inputStyles}
                    />
                    {fieldErrors?.source_name && (
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          mt: 0.5,
                          color: "#d32f2f",
                          fontWeight: 600,
                        }}
                      >
                        {fieldErrors.source_name}
                      </Typography>
                    )}
                    <Typography
                      variant="caption"
                      sx={{ display: "block", mt: 0.5, color: "text.secondary" }}
                    >
                      Máximo 255 caracteres
                    </Typography>
                  </Box>

                  {/* Descripción de la fuente */}
                  <Box>
                    <Typography sx={labelStyles}>Descripción</Typography>
                    <MDInput
                      type="text"
                      multiline
                      rows={3}
                      value={getFieldValue("source_details") || ""}
                      onChange={(e) => handleFieldChange("source_details", e.target.value)}
                      disabled={isDisabled}
                      fullWidth
                      inputProps={{ maxLength: 500 }}
                      sx={inputStyles}
                    />
                    {fieldErrors?.source_details && (
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          mt: 0.5,
                          color: "#d32f2f",
                          fontWeight: 600,
                        }}
                      >
                        {fieldErrors.source_details}
                      </Typography>
                    )}
                    <Typography
                      variant="caption"
                      sx={{ display: "block", mt: 0.5, color: "text.secondary" }}
                    >
                      Máximo 500 caracteres
                    </Typography>
                  </Box>
                </Box>

                {/* Segunda fila: Lugar y Dirección (solo para Invitado/Visitante) */}
                {requiresGuestFields && (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "1fr 1fr",
                      },
                      gap: 2,
                    }}
                  >
                    {/* Lugar */}
                    <Box>
                      <Typography sx={labelStyles}>Lugar *</Typography>
                      <MDInput
                        type="text"
                        value={getFieldValue("location") || ""}
                        onChange={(e) => handleFieldChange("location", e.target.value)}
                        disabled={isDisabled}
                        fullWidth
                        inputProps={{ maxLength: 25 }}
                        error={(isFieldValid && !isFieldValid.location) || !!fieldErrors?.location}
                        sx={inputStyles}
                      />
                      {fieldErrors?.location && (
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            mt: 0.5,
                            color: "#d32f2f",
                            fontWeight: 600,
                          }}
                        >
                          {fieldErrors.location}
                        </Typography>
                      )}
                      <Typography
                        variant="caption"
                        sx={{ display: "block", mt: 0.5, color: "text.secondary" }}
                      >
                        Máximo 25 caracteres • Permitidos: letras, números, comas, puntos, guiones.
                      </Typography>
                    </Box>

                    {/* Dirección */}
                    <Box>
                      <Typography sx={labelStyles}>Dirección *</Typography>
                      <MDInput
                        type="text"
                        value={getFieldValue("address") || ""}
                        onChange={(e) => handleFieldChange("address", e.target.value)}
                        disabled={isDisabled}
                        fullWidth
                        inputProps={{ maxLength: 100 }}
                        error={(isFieldValid && !isFieldValid.address) || !!fieldErrors?.address}
                        sx={inputStyles}
                      />
                      {fieldErrors?.address && (
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            mt: 0.5,
                            color: "#d32f2f",
                            fontWeight: 600,
                          }}
                        >
                          {fieldErrors.address}
                        </Typography>
                      )}
                      <Typography
                        variant="caption"
                        sx={{ display: "block", mt: 0.5, color: "text.secondary" }}
                      >
                        Máximo 100 caracteres • Permitidos: letras, números, comas, puntos, guiones.
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>

              {/* Secciones solo para Invitado/Visitante */}
              {requiresGuestFields && (
                <>
                  {/* Sección: Horarios */}
                  <Box sx={boxCommonStyles}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                        mb: 1.5,
                      }}
                    >
                      <Typography
                        id="modal-modal-description"
                        sx={{
                          color: "black",
                          fontWeight: 500,
                          fontSize: "0.95rem",
                        }}
                      >
                        Establezca el tiempo permitido para habilitar el ingreso de los invitados.
                      </Typography>
                      <HtmlTooltip
                        title={
                          <React.Fragment>
                            {
                              "Especifica cuántas horas y minutos antes y después del evento estará permitido registrar el ingreso de los invitados."
                            }
                          </React.Fragment>
                        }
                      >
                        <HelpOutlineIcon
                          sx={{ ml: 1, fontSize: 18, color: "info.main", cursor: "pointer" }}
                        />
                      </HtmlTooltip>
                    </Box>
                    <TimeSelectComponent
                      label="Check In Time"
                      hoursIn={getFieldValue("check_in_time")}
                      hoursOut={getFieldValue("check_out_time")}
                      minutesIn={getFieldValue("check_in_minute")}
                      minutesOut={getFieldValue("check_out_minute")}
                      onTimeChange={handleFieldChange}
                    />
                  </Box>

                  {/* Sección: Imagen strip */}
                  <Box sx={boxCommonStyles}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        mb: 2,
                      }}
                    >
                      <Typography
                        sx={{
                          ...labelStyles,
                          fontSize: "1rem",
                          fontWeight: 600,
                          color: "info.main",
                        }}
                      >
                        Imagen banner para Wallet pkpass *
                      </Typography>
                      <HtmlTooltip
                        title={
                          <React.Fragment>
                            {
                              "Esta imagen se mostrará en la parte superior del pase de Wallet (pkpass). Es importante que mantenga las dimensiones recomendadas para una visualización óptima."
                            }
                          </React.Fragment>
                        }
                      >
                        <HelpOutlineIcon
                          sx={{ ml: 0.5, fontSize: 18, color: "info.main", cursor: "pointer" }}
                        />
                      </HtmlTooltip>
                    </Box>

                    {/* Vista previa de la imagen actual */}
                    {stripImageUrl && !stripImageFile && (
                      <Box sx={{ mb: 2 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            mb: 0.5,
                            display: "block",
                            color: "text.secondary",
                            fontSize: "0.85rem",
                          }}
                        >
                          Imagen actual (haga clic para descargar):
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            p: 1.5,
                            border: "1px solid #e0e0e0",
                            borderRadius: "8px",
                            backgroundColor: "#f5f5f5",
                            cursor: "pointer",
                            transition: "all 0.3s",
                            "&:hover": {
                              backgroundColor: "#e8f4f8",
                              borderColor: colors.info.main,
                            },
                          }}
                          onClick={() => {
                            const link = document.createElement("a");
                            link.href = stripImageUrl;
                            link.download = "strip_image_actual.png";
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                          }}
                        >
                          <img
                            src={stripImageUrl}
                            alt="Strip actual"
                            style={{
                              maxWidth: "100%",
                              maxHeight: "100px",
                              objectFit: "contain",
                              borderRadius: "4px",
                            }}
                          />
                        </Box>
                      </Box>
                    )}

                    {/* Vista comparativa: Antes y Después */}
                    {stripImageUrl && stripImageFile && (
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: "flex", gap: 1.5 }}>
                          {/* Imagen Actual */}
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant="caption"
                              sx={{
                                mb: 0.5,
                                display: "block",
                                fontWeight: 600,
                                color: "error.main",
                                fontSize: "0.75rem",
                              }}
                            >
                              Actual (Antes)
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                p: 1,
                                border: "2px solid #f44336",
                                borderRadius: "8px",
                                backgroundColor: "#ffebee",
                                cursor: "pointer",
                                transition: "all 0.3s",
                                "&:hover": {
                                  backgroundColor: "#ffcdd2",
                                },
                              }}
                              onClick={() => {
                                const link = document.createElement("a");
                                link.href = stripImageUrl;
                                link.download = "strip_image_actual.png";
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                              }}
                            >
                              <img
                                src={stripImageUrl}
                                alt="Strip actual"
                                style={{
                                  maxWidth: "100%",
                                  maxHeight: "70px",
                                  objectFit: "contain",
                                }}
                              />
                            </Box>
                          </Box>

                          {/* Nueva Imagen */}
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant="caption"
                              sx={{
                                mb: 0.5,
                                display: "block",
                                fontWeight: 600,
                                color: "success.main",
                                fontSize: "0.75rem",
                              }}
                            >
                              Nueva Imagen (Después)
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                p: 1,
                                border: "2px solid #4caf50",
                                borderRadius: "8px",
                                backgroundColor: "#f1f8f4",
                              }}
                            >
                              <img
                                src={URL.createObjectURL(stripImageFile)}
                                alt="Strip nuevo"
                                style={{
                                  maxWidth: "100%",
                                  maxHeight: "70px",
                                  objectFit: "contain",
                                }}
                              />
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    )}

                    {/* Campo de carga de archivo */}
                    <Box sx={{ mb: 1.5 }}>
                      <Typography sx={labelStyles}>Seleccionar Nueva Imagen</Typography>
                      <MDInput
                        type="file"
                        inputProps={{ accept: ".png" }}
                        onChange={(e) => {
                          const selectedFile = e.target.files[0];
                          handleStripImageChange(selectedFile);
                        }}
                        disabled={isDisabled}
                        fullWidth
                        sx={inputStyles}
                      />
                      <Typography
                        variant="caption"
                        sx={{ display: "block", mt: 0.5, color: "text.secondary" }}
                      >
                        <strong>Formato:</strong> PNG (obligatorio) • <strong>Dimensiones:</strong>{" "}
                        423 × 112 píxeles (recomendado) • <strong>Tamaño máximo:</strong> 2 MB
                      </Typography>
                    </Box>
                  </Box>
                </>
              )}
            </MDBox>
          </form>
        </div>
        <Divider variant="middle" sx={{ m: -0.5 }} />

        <MDBox
          style={{
            display: "flex",
            justifyContent: "space-around",
            padding: "8px 0",
          }}
          mb={-2}
        >
          <MDButton color="error" onClick={handleClose}>
            Cancelar
          </MDButton>
          <MDButton
            onClick={handleSubmit}
            type="submit"
            disabled={isDisabled || !isFormModified}
            color="success"
          >
            Guardar
          </MDButton>
        </MDBox>
      </Card>
    </Modal>
  );
}

SourceEdit.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  datosRecuperados: PropTypes.object,
  isDisabled: PropTypes.bool,
  datosEditados: PropTypes.object,
  getFieldValue: PropTypes.func,
  handleFieldChange: PropTypes.func,
  handleSubmit: PropTypes.func,
  isFormModified: PropTypes.bool,
  stripImageUrl: PropTypes.string,
  stripImageFile: PropTypes.object,
  handleStripImageChange: PropTypes.func,
  isFieldValid: PropTypes.object,
  fieldErrors: PropTypes.object,
  recoveredData: PropTypes.object,
};
